import React from 'react';

const SESSION_KEY = 'chunk-refresh-count';

/**
 * Wraps React.lazy with automatic retry on chunk load failure.
 *
 * When a dynamically imported chunk returns a 404 (stale cache after a new
 * deployment), this catches the error, records a reload attempt in
 * sessionStorage, then forces a full page reload so the browser fetches the
 * fresh index.html and new chunk hashes. A counter prevents infinite loops.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const component = await importFn();
      // Successful load — reset the counter
      sessionStorage.removeItem(SESSION_KEY);
      return component;
    } catch (error) {
      const attempts = parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
      if (attempts < 1) {
        sessionStorage.setItem(SESSION_KEY, String(attempts + 1));
        window.location.reload();
        // Return an empty placeholder while the reload happens
        return { default: (() => null) as unknown as T };
      }
      // Already tried reloading — surface the real error
      throw error;
    }
  });
}
